import { useQuery, useMutation, useQueryClient } from 'react-query';
import { predictionArbitrageManagerAxiosApi } from 'src/Config/axiosApi';

export interface PredictionCost {
  _id: string;
  description: string;
  amountUsd: number;
  date: string;
  createdAt: string;
  updatedAt: string;
}

export interface CostsMonthlyEntry {
  month: string;
  totalUsd: number;
  count: number;
}

export interface CostsMonthlyResponse {
  monthly: CostsMonthlyEntry[];
  totalAllTime: number;
}

export interface RealPnlResponse {
  pnlUsd: number;
  totalCostsUsd: number;
  realPnlUsd: number;
  /** Σ of every dividend line, all time. */
  dividendsDistributedUsd: number;
  /** `realPnlUsd − dividendsDistributedUsd`. Negative means we distributed more
   * than we earned — never clamp it, that is the condition worth seeing. */
  dividendsPendingUsd: number;
  monthly: CostsMonthlyEntry[];
}

export interface Partner {
  _id: string;
  name: string;
  active: boolean;
  note?: string;
  legacyUnattributed: boolean;
}

export interface PartnerWithTotals extends Partner {
  totalReceivedUsd: number;
  distributionCount: number;
  lastReceivedAt: string | null;
}

export interface DividendLine {
  _id: string;
  partnerId: string;
  partnerName: string;
  amountUsd: number;
  note?: string;
}

export interface DividendDistribution {
  _id: string;
  settledAt: string;
  totalUsd: number;
  note?: string;
  realPnlSnapshotUsd: number;
  portfolioPnlSnapshotUsd?: number;
  totalCostsSnapshotUsd?: number;
  snapshotIsManual: boolean;
  legacyPaymentSource?: string;
  legacyPaymentPlatform?: string;
  periodStart: string;
  periodEnd: string;
  periodRealPnlUsd: number;
  lines: DividendLine[];
}

export interface DividendsResponse {
  distributions: DividendDistribution[];
  currentPeriod: {
    startDate: string;
    baselineRealPnlUsd: number;
    currentRealPnlUsd: number;
    periodRealPnlUsd: number;
  };
  totalDistributedUsd: number;
  byPartner: Array<{
    partnerId: string;
    partnerName: string;
    totalReceivedUsd: number;
    distributionCount: number;
  }>;
}

const invalidate = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries('prediction-costs');
  qc.invalidateQueries('prediction-costs-monthly');
  qc.invalidateQueries('prediction-real-pnl');
};

export function useCosts() {
  return useQuery(['prediction-costs'], async () => {
    const { data } =
      await predictionArbitrageManagerAxiosApi.get<PredictionCost[]>(
        '/positions/costs'
      );
    return data;
  });
}

export function useCostsMonthly() {
  return useQuery(['prediction-costs-monthly'], async () => {
    const { data } =
      await predictionArbitrageManagerAxiosApi.get<CostsMonthlyResponse>(
        '/positions/costs/monthly'
      );
    return data;
  });
}

export function useRealPnl() {
  return useQuery(
    ['prediction-real-pnl'],
    async () => {
      const { data } =
        await predictionArbitrageManagerAxiosApi.get<RealPnlResponse>(
          '/positions/real-pnl'
        );
      return data;
    },
    { refetchInterval: 30000 }
  );
}

export function useCreateCost() {
  const qc = useQueryClient();
  return useMutation(
    async (body: { description: string; amountUsd: number; date?: string }) => {
      const { data } = await predictionArbitrageManagerAxiosApi.post(
        '/positions/costs',
        body
      );
      return data;
    },
    { onSuccess: () => invalidate(qc) }
  );
}

export function useUpdateCost() {
  const qc = useQueryClient();
  return useMutation(
    async ({
      id,
      ...body
    }: {
      id: string;
      description?: string;
      amountUsd?: number;
      date?: string;
    }) => {
      const { data } = await predictionArbitrageManagerAxiosApi.patch(
        `/positions/costs/${id}`,
        body
      );
      return data;
    },
    { onSuccess: () => invalidate(qc) }
  );
}

export function useDeleteCost() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await predictionArbitrageManagerAxiosApi.delete(
        `/positions/costs/${id}`
      );
      return data;
    },
    { onSuccess: () => invalidate(qc) }
  );
}

// A distribution shifts Dividends Pending (real-pnl) and the per-partner
// totals; the deprecated payment alias can still cascade a cashflow.
const invalidateDividends = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries('prediction-dividends');
  qc.invalidateQueries('prediction-partners');
  qc.invalidateQueries('prediction-real-pnl');
  qc.invalidateQueries('position-cashflows');
};

// Dividend line rows carry a server-resolved partnerName, so renaming or
// deactivating a partner has to refresh the distributions too.
const invalidatePartners = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries('prediction-partners');
  qc.invalidateQueries('prediction-dividends');
};

export function usePartners() {
  return useQuery(
    ['prediction-partners'],
    async () => {
      const { data } =
        await predictionArbitrageManagerAxiosApi.get<PartnerWithTotals[]>(
          '/positions/partners'
        );
      return data;
    },
    { refetchInterval: 30000 }
  );
}

export function useCreatePartner() {
  const qc = useQueryClient();
  return useMutation(
    async (body: { name: string; note?: string }) => {
      const { data } = await predictionArbitrageManagerAxiosApi.post(
        '/positions/partners',
        body
      );
      return data;
    },
    { onSuccess: () => invalidatePartners(qc) }
  );
}

export function useUpdatePartner() {
  const qc = useQueryClient();
  return useMutation(
    async ({
      id,
      ...body
    }: {
      id: string;
      name?: string;
      note?: string;
      active?: boolean;
    }) => {
      const { data } = await predictionArbitrageManagerAxiosApi.patch(
        `/positions/partners/${id}`,
        body
      );
      return data;
    },
    { onSuccess: () => invalidatePartners(qc) }
  );
}

export function useDeletePartner() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await predictionArbitrageManagerAxiosApi.delete(
        `/positions/partners/${id}`
      );
      return data;
    },
    { onSuccess: () => invalidatePartners(qc) }
  );
}

export function useDividends() {
  return useQuery(
    ['prediction-dividends'],
    async () => {
      const { data } =
        await predictionArbitrageManagerAxiosApi.get<DividendsResponse>(
          '/positions/dividends'
        );
      return data;
    },
    { refetchInterval: 30000 }
  );
}

export function useCreateDividendDistribution() {
  const qc = useQueryClient();
  return useMutation(
    async (body: {
      lines?: Array<{ partnerId: string; amountUsd: number; note?: string }>;
      settledAt?: string;
      note?: string;
      monthPnlUsd?: number;
      snapshotOnly?: boolean;
    }) => {
      const { data } = await predictionArbitrageManagerAxiosApi.post(
        '/positions/dividends',
        body
      );
      return data;
    },
    { onSuccess: () => invalidateDividends(qc) }
  );
}

export function useUpdateDividendLine() {
  const qc = useQueryClient();
  return useMutation(
    async ({
      id,
      ...body
    }: {
      id: string;
      partnerId?: string;
      amountUsd?: number;
      note?: string;
    }) => {
      const { data } = await predictionArbitrageManagerAxiosApi.patch(
        `/positions/dividends/lines/${id}`,
        body
      );
      return data;
    },
    { onSuccess: () => invalidateDividends(qc) }
  );
}

export function useDeleteDividendDistribution() {
  const qc = useQueryClient();
  return useMutation(
    async (id: string) => {
      const { data } = await predictionArbitrageManagerAxiosApi.delete(
        `/positions/dividends/${id}`
      );
      return data;
    },
    { onSuccess: () => invalidateDividends(qc) }
  );
}

// Daily P&L snapshot — one row written by the backend cron at 23:59 UTC.
// Attribution fields (`markValueDeltaUsd`, `tradingCashDeltaUsd`, `dailyPnlUsd`)
// are null on the very first snapshot (no prior baseline) AND on any day whose
// `accountScope` differs from the previous row's — a day-over-day diff across a
// changed account set is not a P&L, it is the arithmetic of an account being
// added. The two causes read differently to an operator and are kept apart.
export interface PositionSnapshot {
  pairId?: string;
  platform: 'kalshi' | 'polymarket';
  marketId?: string;
  side: 'yes' | 'no';
  contracts: number;
  avgPrice: number;
  markPrice: number;
  markValueUsd: number;
}

export interface PredictionDailySnapshot {
  date: string;
  positions: PositionSnapshot[];
  openPositionsMarkValueUsd: number;
  openPositionsCostBasisUsd: number;
  kalshiCashUsd: number;
  polymarketCashUsd: number;
  exchangeCashUsd: number;
  cashflowsUsd: number;
  totalPortfolioValueUsd: number;
  /** Realized cost side of daily P&L — drives dailyPnlUsd. */
  costBasisDeltaUsd: number | null;
  /** Informational unrealized drift of still-open positions — NOT in dailyPnlUsd. */
  markValueDeltaUsd: number | null;
  tradingCashDeltaUsd: number | null;
  dailyPnlUsd: number | null;
  /** Sorted `platform:accountId` this row was taken over. */
  accountScope?: string[];
}

/** Why a row's attribution fields are `null`. */
export type DailyPnlGapReason = 'no-baseline' | 'scope-changed';

export const DAILY_PNL_GAP_LABEL: Record<DailyPnlGapReason, string> = {
  // Deliberately does NOT say "primer snapshot": this reason also covers a row
  // the backend simply did not attribute, which can sit anywhere in the series.
  'no-baseline': 'sin P&L atribuido — no hay un día anterior comparable',
  'scope-changed':
    'no comparable — cambió el conjunto de cuentas respecto del día anterior, así que un diff día a día mediría el alta de la cuenta, no P&L'
};

const sameScope = (a: string[] | undefined, b: string[] | undefined): boolean => {
  if (!Array.isArray(a) || !Array.isArray(b)) return true;
  if (a.length !== b.length) return false;
  return a.every((value, i) => value === b[i]);
};

/**
 * Which of the two causes produced a null on `row`, given the row before it.
 * `null` when the row carries a real number and there is no gap to explain.
 *
 * A missing `accountScope` on either row means the backend did not report a
 * scope, and a scope that was never stated cannot be claimed to have changed —
 * such a row falls back to `no-baseline` rather than inventing a scope change.
 */
export function dailyPnlGapReason(
  row: Pick<PredictionDailySnapshot, 'dailyPnlUsd' | 'accountScope'>,
  previous: Pick<PredictionDailySnapshot, 'accountScope'> | undefined
): DailyPnlGapReason | null {
  if (row.dailyPnlUsd != null) return null;
  if (previous == null) return 'no-baseline';
  return sameScope(row.accountScope, previous.accountScope)
    ? 'no-baseline'
    : 'scope-changed';
}

export function useDailyPnl(from?: string, to?: string) {
  return useQuery(
    ['prediction-daily-pnl', from ?? null, to ?? null],
    async () => {
      const params: Record<string, string> = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const { data } = await predictionArbitrageManagerAxiosApi.get<
        PredictionDailySnapshot[]
      >('/positions/daily-pnl', { params });
      return data;
    },
    { refetchInterval: 30000 }
  );
}
